import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  FormControlLabel,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ArticleIcon from '@mui/icons-material/Article';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DataObjectIcon from '@mui/icons-material/DataObject';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DownloadIcon from '@mui/icons-material/Download';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import {
  pepApiRequest,
  ContentType,
  RequestMethod,
  useStatefulApi,
} from 'src/services';
import { AlertType, useAlert } from 'src/components/AlertsProvider';
import ModalDialog from 'src/components/ModalDialog';
import { generateApiPath } from 'src/utils';
import { API_UPLOAD_DTDL_MODEL } from '../../constants';

// ── Constants ──────────────────────────────────────────────────────────────

const CONTEXTS: Record<string, string> = {
  v2: 'dtmi:dtdl:context;2',
  v3: 'dtmi:dtdl:context;3',
};

const DEFAULT_EXTENDS = 'dtmi:digitaltwins:isa95:HierarchyScope;1';

const PRIMITIVE_SCHEMA_OPTIONS = [
  'boolean',
  'dateTime',
  'double',
  'float',
  'integer',
  'string',
];

const SCHEMA_KIND_OPTIONS = [
  { value: 'primitive', label: 'Primitive' },
  { value: 'object', label: 'Object' },
  { value: 'enum', label: 'Enum' },
  { value: 'map', label: 'Map' },
  { value: 'array', label: 'Array (v3 only)' },
];

// ── Types ──────────────────────────────────────────────────────────────────

interface ObjectField {
  id: string;
  name: string;
  schema: string;
}

interface EnumValueItem {
  id: string;
  name: string;
  enumValue: string;
}

interface PropertyItem {
  id: string;
  name: string;
  writable: boolean;
  schemaKind: string;
  primitiveSchema: string;
  objectFields: ObjectField[];
  enumValueSchema: string;
  enumValues: EnumValueItem[];
  mapKeySchema: string;
  mapValueSchema: string;
  arrayElementSchema: string;
}

interface RelationshipItem {
  id: string;
  name: string;
  target: string;
  displayName: string;
  relationshipId: string;
  properties: PropertyItem[];
}

interface ComponentItem {
  id: string;
  name: string;
  schema: string;
}

interface InterfaceItem {
  id: string;
  modelId: string;
  displayName: string;
  extendsText: string;
  properties: PropertyItem[];
  relationships: RelationshipItem[];
  components: ComponentItem[];
}

type ValidationVisibility = 'all' | 'errors' | 'warnings';

interface SeverityMessages {
  errors: string[];
  warnings: string[];
}

interface ValidationBucket {
  modelId: SeverityMessages;
  displayName: SeverityMessages;
  extendsText: SeverityMessages;
  summary: SeverityMessages;
  properties: Record<string, Record<string, SeverityMessages>>;
  relationships: Record<string, Record<string, SeverityMessages>>;
  components: Record<string, Record<string, SeverityMessages>>;
}

interface InterfaceValidationStatus {
  errorCount: number;
  warningCount: number;
  hasErrors: boolean;
  hasWarnings: boolean;
}

// ── Factory helpers ────────────────────────────────────────────────────────

const createObjectField = (seed: Partial<ObjectField> = {}): ObjectField => ({
  id: crypto.randomUUID(),
  name: seed.name ?? '',
  schema: seed.schema ?? 'string',
});

const createEnumValue = (seed: Partial<EnumValueItem> = {}): EnumValueItem => ({
  id: crypto.randomUUID(),
  name: seed.name ?? '',
  enumValue: seed.enumValue ?? '',
});

const createProperty = (seed: Partial<PropertyItem> = {}): PropertyItem => ({
  id: crypto.randomUUID(),
  name: seed.name ?? '',
  writable: seed.writable ?? false,
  schemaKind: seed.schemaKind ?? 'primitive',
  primitiveSchema: seed.primitiveSchema ?? 'string',
  objectFields: seed.objectFields ?? [createObjectField()],
  enumValueSchema: seed.enumValueSchema ?? 'string',
  enumValues: seed.enumValues ?? [createEnumValue()],
  mapKeySchema: seed.mapKeySchema ?? 'string',
  mapValueSchema: seed.mapValueSchema ?? 'string',
  arrayElementSchema: seed.arrayElementSchema ?? 'string',
});

const createRelationship = (): RelationshipItem => ({
  id: crypto.randomUUID(),
  name: '',
  target: '',
  displayName: '',
  relationshipId: '',
  properties: [],
});

const createComponent = (): ComponentItem => ({
  id: crypto.randomUUID(),
  name: '',
  schema: '',
});

const createInterface = (seed: Partial<InterfaceItem> = {}): InterfaceItem => ({
  id: crypto.randomUUID(),
  modelId: seed.modelId ?? '',
  displayName: seed.displayName ?? '',
  extendsText: seed.extendsText ?? DEFAULT_EXTENDS,
  properties: seed.properties ?? [],
  relationships: seed.relationships ?? [],
  components: seed.components ?? [],
});

// ── Utility helpers ────────────────────────────────────────────────────────

const parseExtends = (text: string): string[] =>
  text
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);

const toEnumValue = (
  valueSchema: string,
  value: string,
  contextLabel: string
): number | string => {
  if (valueSchema === 'integer') {
    const parsed = Number.parseInt(value, 10);
    if (Number.isNaN(parsed)) {
      throw new Error(`${contextLabel}: enumValue must be an integer.`);
    }
    return parsed;
  }
  return value;
};

const buildPropertySchema = (
  property: PropertyItem,
  dtdlVersion: string,
  contextLabel: string
): any => {
  if (property.schemaKind === 'primitive') return property.primitiveSchema;

  if (property.schemaKind === 'array') {
    if (dtdlVersion !== 'v3') {
      throw new Error(
        `${contextLabel}: Array schema is only supported in DTDL v3.`
      );
    }
    return { '@type': 'Array', elementSchema: property.arrayElementSchema };
  }

  if (property.schemaKind === 'object') {
    const fields = property.objectFields
      .filter((f) => f.name.trim())
      .map((f) => ({ name: f.name.trim(), schema: f.schema }));
    if (fields.length === 0) {
      throw new Error(
        `${contextLabel}: Object schema requires at least one field.`
      );
    }
    return { '@type': 'Object', fields };
  }

  if (property.schemaKind === 'enum') {
    const enumValues = property.enumValues
      .filter((item) => item.name.trim() && String(item.enumValue).trim())
      .map((item) => ({
        name: item.name.trim(),
        enumValue: toEnumValue(
          property.enumValueSchema,
          String(item.enumValue).trim(),
          contextLabel
        ),
      }));
    if (enumValues.length === 0) {
      throw new Error(
        `${contextLabel}: Enum schema requires at least one enum value.`
      );
    }
    return {
      '@type': 'Enum',
      valueSchema: property.enumValueSchema,
      enumValues,
    };
  }

  if (property.schemaKind === 'map') {
    return {
      '@type': 'Map',
      mapKey: { name: 'key', schema: property.mapKeySchema },
      mapValue: { name: 'value', schema: property.mapValueSchema },
    };
  }

  return property.primitiveSchema;
};

const toPropertyContent = (
  property: PropertyItem,
  dtdlVersion: string,
  contextLabel: string
): any => {
  const item: any = {
    '@type': 'Property',
    name: property.name.trim(),
    schema: buildPropertySchema(property, dtdlVersion, contextLabel),
  };
  if (property.writable) item.writable = true;
  return item;
};

const orderInterfacesByDependencies = (interfacesInInputOrder: any[]) => {
  const idToInterface = new Map(
    interfacesInInputOrder.map((model) => [model['@id'], model])
  );
  const dependents = new Map<string, string[]>();
  const inDegree = new Map<string, number>();

  interfacesInInputOrder.forEach((model) => {
    const currentId = model['@id'];
    let extendsValues: any[];
    if (Array.isArray(model.extends)) {
      extendsValues = model.extends;
    } else if (model.extends) {
      extendsValues = [model.extends];
    } else {
      extendsValues = [];
    }
    const componentDeps = (model.contents ?? [])
      .filter((item: any) => item['@type'] === 'Component')
      .map((item: any) => item.schema);
    const deps = [...extendsValues, ...componentDeps].filter(
      (dep: string) => dep && dep !== currentId && idToInterface.has(dep)
    );
    inDegree.set(currentId, deps.length);
    deps.forEach((dep: string) => {
      dependents.set(dep, [...(dependents.get(dep) ?? []), currentId]);
    });
  });

  const queue = interfacesInInputOrder
    .map((model) => model['@id'] as string)
    .filter((id) => (inDegree.get(id) ?? 0) === 0);

  const orderedIds: string[] = [];
  while (queue.length > 0) {
    const current = queue.shift()!;
    orderedIds.push(current);
    (dependents.get(current) ?? []).forEach((child) => {
      const nextValue = (inDegree.get(child) ?? 0) - 1;
      inDegree.set(child, nextValue);
      if (nextValue === 0) queue.push(child);
    });
  }

  const unresolved = interfacesInInputOrder
    .map((model) => model['@id'] as string)
    .filter((id) => !orderedIds.includes(id));

  const finalOrder = [...orderedIds, ...unresolved];
  const orderedModels = finalOrder
    .map((id) => idToInterface.get(id))
    .filter(Boolean);
  return {
    orderedModels,
    orderedIds: finalOrder,
    cycleDetected: unresolved.length > 0,
  };
};

// ── Validation helpers ────────────────────────────────────────────────────

const createSeverityMessages = (): SeverityMessages => ({
  errors: [],
  warnings: [],
});

const createValidationBucket = (): ValidationBucket => ({
  modelId: createSeverityMessages(),
  displayName: createSeverityMessages(),
  extendsText: createSeverityMessages(),
  summary: createSeverityMessages(),
  properties: {},
  relationships: {},
  components: {},
});

const addValidationMessage = (
  bucket: any,
  key: string,
  severity: 'errors' | 'warnings',
  message: string
) => {
  bucket[key] = {
    ...(bucket[key] ?? createSeverityMessages()),
    [severity]: [
      ...((bucket[key] ?? createSeverityMessages())[severity] ?? []),
      message,
    ],
  };
};

const addNestedValidationMessage = (
  bucket: any,
  itemId: string,
  key: string,
  severity: 'errors' | 'warnings',
  message: string
) => {
  bucket[itemId] = {
    ...(bucket[itemId] ?? {}),
    [key]: {
      ...((bucket[itemId] ?? {})[key] ?? createSeverityMessages()),
      [severity]: [
        ...(((bucket[itemId] ?? {})[key] ?? createSeverityMessages())[
          severity
        ] ?? []),
        message,
      ],
    },
  };
};

const countSeverityMessages = (messages: SeverityMessages | undefined) => ({
  errorCount: messages?.errors?.length ?? 0,
  warningCount: messages?.warnings?.length ?? 0,
});

const summarizeValidationBucket = (
  bucket: ValidationBucket
): InterfaceValidationStatus => {
  let errorCount = 0;
  let warningCount = 0;

  [
    bucket.modelId,
    bucket.displayName,
    bucket.extendsText,
    bucket.summary,
  ].forEach((messages) => {
    const counts = countSeverityMessages(messages);
    errorCount += counts.errorCount;
    warningCount += counts.warningCount;
  });

  [bucket.properties, bucket.relationships, bucket.components].forEach(
    (collection) => {
      Object.values(collection).forEach((item) => {
        Object.values(item).forEach((messages) => {
          const counts = countSeverityMessages(messages as SeverityMessages);
          errorCount += counts.errorCount;
          warningCount += counts.warningCount;
        });
      });
    }
  );

  return {
    errorCount,
    warningCount,
    hasErrors: errorCount > 0,
    hasWarnings: warningCount > 0,
  };
};

// ── Sub-components ────────────────────────────────────────────────────────

const FieldWarnings = ({
  messages,
  visibility = 'all',
}: {
  messages: SeverityMessages | any;
  visibility?: ValidationVisibility;
}) => {
  const errorMessages: string[] = messages?.errors ?? [];
  const warningMessages: string[] = messages?.warnings ?? [];
  const shownErrors = visibility === 'warnings' ? [] : errorMessages;
  const shownWarnings = visibility === 'errors' ? [] : warningMessages;

  if (shownErrors.length === 0 && shownWarnings.length === 0) return null;

  return (
    <Stack spacing={1} sx={{ mt: 1 }}>
      {shownErrors.map((msg) => (
        <Alert key={`error-${msg}`} severity="error" variant="outlined">
          {msg}
        </Alert>
      ))}
      {shownWarnings.map((msg) => (
        <Alert key={`warning-${msg}`} severity="warning" variant="outlined">
          {msg}
        </Alert>
      ))}
    </Stack>
  );
};

// ── Main Component ────────────────────────────────────────────────────────

export default function DTDLModelGenerator() {
  const { solutionId } = useParams();
  const { showAlert } = useAlert();
  const [dtdlVersion, setDtdlVersion] = useState('v2');
  const [strictAzureMode] = useState(true);
  const [interfaces, setInterfaces] = useState<InterfaceItem[]>([
    createInterface({
      modelId: 'dtmi:example:Interface1;1',
      displayName: 'Interface 1',
      properties: [],
    }),
  ]);
  const [activeInterfaceId, setActiveInterfaceId] = useState(
    () => interfaces[0].id
  );
  const [generatedJson, setGeneratedJson] = useState('');
  const [dependencyOrder, setDependencyOrder] = useState<string[]>([]);
  const [statusMessage, setStatusMessage] = useState('');
  const [error, setError] = useState('');
  const [validationVisibility, setValidationVisibility] =
    useState<ValidationVisibility>('all');
  const [uploadResult, setUploadResult] = useState<{
    statusCode: number;
    responseBody: string;
  } | null>(null);

  // ── Upload modal state ────────────────────────────────────────────────
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [isModalUploading, setIsModalUploading] = useState(false);
  const [uploadModalError, setUploadModalError] = useState('');
  const [uploadModalResult, setUploadModalResult] = useState<{
    statusCode: number;
    responseBody: string;
  } | null>(null);

  const openUploadModal = () => {
    setUploadFile(null);
    setUploadModalError('');
    setUploadModalResult(null);
    setUploadModalOpen(true);
  };

  const useGeneratedJsonAsUploadFile = () => {
    if (!generatedJson) {
      setUploadModalError(
        'Generate DTDL JSON first before using it for upload.'
      );
      return;
    }
    const file = new File([generatedJson], 'dtdl-models.json', {
      type: 'application/json',
    });
    setUploadFile(file);
    setUploadModalError('');
  };

  const handleModalUpload = async () => {
    setUploadModalError('');
    setUploadModalResult(null);
    if (!uploadFile) {
      setUploadModalError(
        'Select a JSON file or use the generated JSON before uploading.'
      );
      return;
    }
    setIsModalUploading(true);
    try {
      const response = await pepApiRequest({
        method: RequestMethod.Post,
        url: generateApiPath(API_UPLOAD_DTDL_MODEL, { solutionId }),
        contentType: ContentType.FormData,
        params: { SerializedJsonModel: uploadFile },
        skipErrorAlert: true,
      });
      setUploadModalResult({
        statusCode: 200,
        responseBody:
          typeof response === 'object'
            ? JSON.stringify(response, null, 2)
            : String(response ?? '(empty response)'),
      });
      showAlert({
        type: AlertType.Success,
        title: 'DTDL Model uploaded',
        text: 'DTDL model has been uploaded to ADT successfully.',
      });
    } catch (err: any) {
      const statusCode = err?.status ?? 500;
      const bodyPromise =
        typeof err?.json === 'function'
          ? err.json().catch(() => err?.statusText ?? 'Upload failed')
          : Promise.resolve(String(err ?? 'Upload failed'));
      const body = await bodyPromise;
      const bodyStr =
        typeof body === 'object' ? JSON.stringify(body, null, 2) : String(body);

      // Extract title and detail from API error response for the notification
      let alertTitle = `Upload failed (${statusCode})`;
      let alertText = 'DTDL model upload to ADT failed.';
      if (typeof body === 'object' && body !== null) {
        const firstError = Array.isArray(body.errors) ? body.errors[0] : null;
        if (firstError?.title) {
          alertTitle = firstError.title;
        }
        if (firstError?.detail) {
          alertText = firstError.detail;
        }
      }
      showAlert({
        type: AlertType.Error,
        title: alertTitle,
        text: alertText,
      });

      let userMessage = bodyStr;
      if (statusCode === 409) {
        userMessage = `A model with this DTMI already exists in Azure Digital Twins.\n\nTo update it, increment the version number in the Model DTMI (e.g. change ;1 to ;2), then re-generate and upload.\n\nServer response:\n${bodyStr}`;
      }
      setUploadModalResult({ statusCode, responseBody: userMessage });
    } finally {
      setIsModalUploading(false);
    }
  };

  // Track whether the model has been edited since last generation
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    // Clear generated output when the model is edited so stale JSON can't be uploaded
    if (generatedJson) {
      setGeneratedJson('');
      setDependencyOrder([]);
      setStatusMessage('');
      setUploadResult(null);
    }
  }, [interfaces, dtdlVersion]);

  const [handleUploadModel, isUploading] = useStatefulApi(
    () => {
      if (!generatedJson) {
        return Promise.reject(
          new Error('Generate DTDL JSON before uploading.')
        );
      }
      if (validation.hasErrors) {
        return Promise.reject(
          new Error(
            'Model has validation errors. Resolve them and re-generate before uploading.'
          )
        );
      }
      setUploadResult(null);
      const file = new File([generatedJson], 'dtdl-models.json', {
        type: 'application/json',
      });
      return pepApiRequest({
        method: RequestMethod.Post,
        url: generateApiPath(API_UPLOAD_DTDL_MODEL, { solutionId }),
        contentType: ContentType.FormData,
        params: { SerializedJsonModel: file },
        skipErrorAlert: true,
      });
    },
    (response) => {
      setUploadResult({
        statusCode: 200,
        responseBody:
          typeof response === 'object'
            ? JSON.stringify(response, null, 2)
            : String(response ?? '(empty response)'),
      });
      showAlert({
        type: AlertType.Success,
        title: 'DTDL Model uploaded',
        text: 'DTDL model has been uploaded to ADT successfully.',
      });
    },
    (err: any) => {
      const statusCode = err?.status ?? 500;
      const bodyPromise =
        typeof err?.json === 'function'
          ? err.json().catch(() => err?.statusText ?? 'Upload failed')
          : Promise.resolve(String(err ?? 'Upload failed'));
      bodyPromise.then((body: any) => {
        const bodyStr =
          typeof body === 'object'
            ? JSON.stringify(body, null, 2)
            : String(body);

        // Extract title and detail from API error response for the notification
        let alertTitle = `Upload failed (${statusCode})`;
        let alertText = 'DTDL model upload to ADT failed.';
        if (typeof body === 'object' && body !== null) {
          const firstError = Array.isArray(body.errors) ? body.errors[0] : null;
          if (firstError?.title) {
            alertTitle = firstError.title;
          }
          if (firstError?.detail) {
            alertText = firstError.detail;
          }
        }

        showAlert({
          type: AlertType.Error,
          title: alertTitle,
          text: alertText,
        });

        let userMessage = bodyStr;
        if (statusCode === 409) {
          userMessage = `A model with this DTMI already exists in Azure Digital Twins.\n\nTo update it, increment the version number in the Model DTMI (e.g. change ;1 to ;2), then re-generate and upload.\n\nServer response:\n${bodyStr}`;
        }

        setUploadResult({ statusCode, responseBody: userMessage });
      });
    }
  );

  const interfaceById = useMemo(
    () => new Map(interfaces.map((entry) => [entry.id, entry])),
    [interfaces]
  );

  const activeInterface = interfaceById.get(activeInterfaceId) ?? interfaces[0];

  const knownInterfaceDtmis = useMemo(
    () =>
      interfaces
        .map((entry) => entry.modelId.trim())
        .filter(
          (value, index, array) => value && array.indexOf(value) === index
        ),
    [interfaces]
  );

  // ── Validation ──────────────────────────────────────────────────────────

  const validation = useMemo(() => {
    const interfaceValidations: Record<string, ValidationBucket> = {};
    const interfaceStatus: Record<string, InterfaceValidationStatus> = {};
    const modelIdCounts = interfaces.reduce<Record<string, number>>(
      (counts, entry) => {
        const value = entry.modelId.trim();
        if (!value) return counts;
        return { ...counts, [value]: (counts[value] ?? 0) + 1 };
      },
      {}
    );

    interfaces.forEach((entry) => {
      const bucket = createValidationBucket();
      const trimmedModelId = entry.modelId.trim();
      const trimmedDisplayName = entry.displayName.trim();
      const extendsValues = parseExtends(entry.extendsText);

      if (!trimmedModelId) {
        addValidationMessage(
          bucket,
          'modelId',
          'errors',
          'Model DTMI is required.'
        );
      } else {
        if (!trimmedModelId.startsWith('dtmi:')) {
          addValidationMessage(
            bucket,
            'modelId',
            'errors',
            'DTMI must start with dtmi:.'
          );
        }
        if ((modelIdCounts[trimmedModelId] ?? 0) > 1) {
          addValidationMessage(
            bucket,
            'modelId',
            'errors',
            'This interface DTMI is duplicated in another interface.'
          );
        }
      }

      if (!trimmedDisplayName) {
        addValidationMessage(
          bucket,
          'displayName',
          'errors',
          'Display name is required.'
        );
      }

      extendsValues.forEach((value) => {
        if (!value.startsWith('dtmi:')) {
          addValidationMessage(
            bucket,
            'extendsText',
            'errors',
            `Extends value ${value} must start with dtmi:.`
          );
        }
      });

      if (strictAzureMode && dtdlVersion === 'v2' && extendsValues.length > 2) {
        addValidationMessage(
          bucket,
          'extendsText',
          'errors',
          'Azure Digital Twins with DTDL v2 allows at most two immediate extends interfaces.'
        );
      }

      const contentNameCounts: Record<string, number> = {};
      const propertyNameCounts: Record<string, number> = {};

      entry.properties.forEach((property) => {
        const trimmedName = property.name.trim();
        if (trimmedName) {
          contentNameCounts[trimmedName] =
            (contentNameCounts[trimmedName] ?? 0) + 1;
          propertyNameCounts[trimmedName] =
            (propertyNameCounts[trimmedName] ?? 0) + 1;
        }
      });

      entry.relationships.forEach((relationship) => {
        const trimmedName = relationship.name.trim();
        if (trimmedName) {
          contentNameCounts[trimmedName] =
            (contentNameCounts[trimmedName] ?? 0) + 1;
        }
      });

      entry.components.forEach((component) => {
        const trimmedName = component.name.trim();
        if (trimmedName) {
          contentNameCounts[trimmedName] =
            (contentNameCounts[trimmedName] ?? 0) + 1;
        }
      });

      entry.properties.forEach((property) => {
        const trimmedName = property.name.trim();
        const hasAnyDraftValue =
          trimmedName ||
          property.schemaKind !== 'primitive' ||
          property.writable;

        if (!trimmedName) {
          addNestedValidationMessage(
            bucket.properties,
            property.id,
            'name',
            'warnings',
            'Unnamed property will be ignored during generation.'
          );
        } else {
          if ((propertyNameCounts[trimmedName] ?? 0) > 1) {
            addNestedValidationMessage(
              bucket.properties,
              property.id,
              'name',
              'errors',
              'Duplicate property name in this interface.'
            );
          }
          if ((contentNameCounts[trimmedName] ?? 0) > 1) {
            addNestedValidationMessage(
              bucket.properties,
              property.id,
              'name',
              'errors',
              'Name conflicts with another property, relationship, or component.'
            );
          }
        }

        if (property.schemaKind === 'array' && dtdlVersion !== 'v3') {
          addNestedValidationMessage(
            bucket.properties,
            property.id,
            'schemaKind',
            'errors',
            'Array schema requires DTDL v3.'
          );
        }

        if (property.schemaKind === 'object') {
          const fieldCounts: Record<string, number> = {};
          property.objectFields.forEach((field) => {
            const fieldName = field.name.trim();
            if (fieldName)
              fieldCounts[fieldName] = (fieldCounts[fieldName] ?? 0) + 1;
          });
          property.objectFields.forEach((field) => {
            const fieldName = field.name.trim();
            if (!fieldName) {
              addNestedValidationMessage(
                bucket.properties,
                property.id,
                `field-${field.id}`,
                'warnings',
                'Unnamed object field will be ignored during generation.'
              );
            } else if ((fieldCounts[fieldName] ?? 0) > 1) {
              addNestedValidationMessage(
                bucket.properties,
                property.id,
                `field-${field.id}`,
                'errors',
                'Duplicate object field name in this property.'
              );
            }
          });
          const validFieldCount = property.objectFields.filter((f) =>
            f.name.trim()
          ).length;
          if (trimmedName && validFieldCount === 0) {
            addNestedValidationMessage(
              bucket.properties,
              property.id,
              'schemaKind',
              'errors',
              'Object schema requires at least one named field.'
            );
          }
        }

        if (property.schemaKind === 'enum') {
          const enumNameCounts: Record<string, number> = {};
          property.enumValues.forEach((ev) => {
            const enumName = ev.name.trim();
            if (enumName)
              enumNameCounts[enumName] = (enumNameCounts[enumName] ?? 0) + 1;
          });
          property.enumValues.forEach((ev) => {
            const enumName = ev.name.trim();
            const rawValue = String(ev.enumValue).trim();
            if (!enumName) {
              addNestedValidationMessage(
                bucket.properties,
                property.id,
                `enum-${ev.id}`,
                'warnings',
                'Unnamed enum entry will be ignored during generation.'
              );
            } else if ((enumNameCounts[enumName] ?? 0) > 1) {
              addNestedValidationMessage(
                bucket.properties,
                property.id,
                `enum-${ev.id}`,
                'errors',
                'Duplicate enum name in this property.'
              );
            }
            if (!rawValue) {
              addNestedValidationMessage(
                bucket.properties,
                property.id,
                `enum-${ev.id}`,
                'warnings',
                'Enum entry without a value will be ignored during generation.'
              );
            }
            if (
              property.enumValueSchema === 'integer' &&
              rawValue &&
              Number.isNaN(Number.parseInt(rawValue, 10))
            ) {
              addNestedValidationMessage(
                bucket.properties,
                property.id,
                `enum-${ev.id}`,
                'errors',
                'Enum value must be a valid integer.'
              );
            }
          });
          const validEnumCount = property.enumValues.filter(
            (ev) => ev.name.trim() && String(ev.enumValue).trim()
          ).length;
          if (trimmedName && validEnumCount === 0) {
            addNestedValidationMessage(
              bucket.properties,
              property.id,
              'schemaKind',
              'errors',
              'Enum schema requires at least one complete enum entry.'
            );
          }
        }
      });

      entry.relationships.forEach((relationship) => {
        const trimmedName = relationship.name.trim();
        const hasRelationshipDraft =
          trimmedName ||
          relationship.target.trim() ||
          relationship.displayName.trim() ||
          relationship.relationshipId.trim();
        if (!trimmedName) {
          addNestedValidationMessage(
            bucket.relationships,
            relationship.id,
            'name',
            'warnings',
            'Unnamed relationship will be ignored during generation.'
          );
        } else if ((contentNameCounts[trimmedName] ?? 0) > 1) {
          addNestedValidationMessage(
            bucket.relationships,
            relationship.id,
            'name',
            'errors',
            'Name conflicts with another property, relationship, or component.'
          );
        }
        if (
          relationship.target.trim() &&
          !relationship.target.trim().startsWith('dtmi:')
        ) {
          addNestedValidationMessage(
            bucket.relationships,
            relationship.id,
            'target',
            'errors',
            'Relationship target must start with dtmi:.'
          );
        } else if (
          relationship.target.trim() &&
          !knownInterfaceDtmis.includes(relationship.target.trim())
        ) {
          addNestedValidationMessage(
            bucket.relationships,
            relationship.id,
            'target',
            'warnings',
            'Relationship target is external to the current authoring set.'
          );
        }

        const relPropCounts: Record<string, number> = {};
        relationship.properties.forEach((p) => {
          const pn = p.name.trim();
          if (pn) relPropCounts[pn] = (relPropCounts[pn] ?? 0) + 1;
        });
        relationship.properties.forEach((p) => {
          const pn = p.name.trim();
          if (!pn) {
            addNestedValidationMessage(
              bucket.relationships,
              relationship.id,
              `property-${p.id}`,
              'warnings',
              'Unnamed relationship property will be ignored during generation.'
            );
          } else if ((relPropCounts[pn] ?? 0) > 1) {
            addNestedValidationMessage(
              bucket.relationships,
              relationship.id,
              `property-${p.id}`,
              'errors',
              'Duplicate relationship property name.'
            );
          }
        });
      });

      entry.components.forEach((component) => {
        const trimmedName = component.name.trim();
        const trimmedSchema = component.schema.trim();
        if (!trimmedName) {
          addNestedValidationMessage(
            bucket.components,
            component.id,
            'name',
            'warnings',
            'Unnamed component will be ignored during generation.'
          );
        } else if ((contentNameCounts[trimmedName] ?? 0) > 1) {
          addNestedValidationMessage(
            bucket.components,
            component.id,
            'name',
            'errors',
            'Name conflicts with another property, relationship, or component.'
          );
        }
        if (!trimmedSchema) {
          addNestedValidationMessage(
            bucket.components,
            component.id,
            'schema',
            'warnings',
            'Component without a schema DTMI will be ignored during generation.'
          );
        } else if (!trimmedSchema.startsWith('dtmi:')) {
          addNestedValidationMessage(
            bucket.components,
            component.id,
            'schema',
            'errors',
            'Component schema must start with dtmi:.'
          );
        } else if (!knownInterfaceDtmis.includes(trimmedSchema)) {
          addNestedValidationMessage(
            bucket.components,
            component.id,
            'schema',
            'warnings',
            'Component schema is external to the current authoring set.'
          );
        }
      });

      const interfaceConflictCount = Object.values(contentNameCounts).filter(
        (count) => count > 1
      ).length;
      if (interfaceConflictCount > 0) {
        addValidationMessage(
          bucket,
          'summary',
          'errors',
          'This interface has duplicate content names across properties, relationships, or components.'
        );
      }

      interfaceValidations[entry.id] = bucket;
      interfaceStatus[entry.id] = summarizeValidationBucket(bucket);
    });

    if (strictAzureMode) {
      const idToInterface = new Map(
        interfaces
          .map((entry) => ({
            id: entry.id,
            modelId: entry.modelId.trim(),
            extendsValues: parseExtends(entry.extendsText),
            components: entry.components,
          }))
          .filter((entry) => entry.modelId)
          .map((entry) => [entry.modelId, entry])
      );

      const usedAsComponent = new Set<string>();
      interfaces.forEach((entry) => {
        entry.components.forEach((component) => {
          const schema = component.schema.trim();
          if (schema) usedAsComponent.add(schema);
        });
      });

      interfaces.forEach((entry) => {
        const modelId = entry.modelId.trim();
        if (!modelId || !usedAsComponent.has(modelId)) return;
        const hasChildComponents = entry.components.some(
          (c) => c.name.trim() && c.schema.trim()
        );
        if (hasChildComponents) {
          addValidationMessage(
            interfaceValidations[entry.id],
            'summary',
            'errors',
            'Azure Digital Twins supports only one level of component nesting.'
          );
        }
      });

      const parentGraph = new Map<string, string[]>();
      idToInterface.forEach((entry, modelId) => {
        const parents = entry.extendsValues.filter((pid) =>
          idToInterface.has(pid)
        );
        parentGraph.set(modelId, parents);
      });

      const depthMemo = new Map<string, number>();
      const depthStack = new Set<string>();

      const getDepth = (modelId: string): number => {
        if (depthMemo.has(modelId)) return depthMemo.get(modelId)!;
        if (depthStack.has(modelId)) return Number.POSITIVE_INFINITY;
        depthStack.add(modelId);
        const parents = parentGraph.get(modelId) ?? [];
        const parentDepth = parents.length
          ? Math.max(...parents.map((p) => getDepth(p)))
          : 0;
        const depth =
          parentDepth === Number.POSITIVE_INFINITY
            ? Number.POSITIVE_INFINITY
            : parentDepth + 1;
        depthMemo.set(modelId, depth);
        depthStack.delete(modelId);
        return depth;
      };

      interfaces.forEach((entry) => {
        const modelId = entry.modelId.trim();
        if (!modelId || !idToInterface.has(modelId)) return;
        const depth = getDepth(modelId);
        if (depth === Number.POSITIVE_INFINITY) {
          addValidationMessage(
            interfaceValidations[entry.id],
            'summary',
            'errors',
            'Inheritance cycle detected in extends chain.'
          );
        } else if (depth > 10) {
          addValidationMessage(
            interfaceValidations[entry.id],
            'summary',
            'errors',
            'Extends hierarchy depth exceeds Azure Digital Twins limit of 10.'
          );
        }
      });

      interfaces.forEach((entry) => {
        interfaceStatus[entry.id] = summarizeValidationBucket(
          interfaceValidations[entry.id]
        );
      });
    }

    return {
      interfaces: interfaceValidations,
      interfaceStatus,
      hasWarnings: Object.values(interfaceStatus).some((s) => s.hasWarnings),
      hasErrors: Object.values(interfaceStatus).some((s) => s.hasErrors),
    };
  }, [dtdlVersion, interfaces, knownInterfaceDtmis, strictAzureMode]);

  const activeValidation =
    validation.interfaces[activeInterface.id] ?? createValidationBucket();
  const activeStatus: InterfaceValidationStatus = validation.interfaceStatus[
    activeInterface.id
  ] ?? { errorCount: 0, warningCount: 0, hasErrors: false, hasWarnings: false };

  // ── Actions ─────────────────────────────────────────────────────────────

  const updateInterface = useCallback(
    (
      interfaceId: string,
      updater: Partial<InterfaceItem> | ((i: InterfaceItem) => InterfaceItem)
    ) => {
      setInterfaces((current) =>
        current.map((entry) => {
          if (entry.id !== interfaceId) return entry;
          return typeof updater === 'function'
            ? updater(entry)
            : { ...entry, ...updater };
        })
      );
    },
    []
  );

  const removeInterface = useCallback(
    (interfaceId: string) => {
      if (interfaces.length <= 1) {
        setError('At least one interface is required.');
        return;
      }
      const next = interfaces.filter((entry) => entry.id !== interfaceId);
      setInterfaces(next);
      if (activeInterfaceId === interfaceId) setActiveInterfaceId(next[0].id);
    },
    [interfaces, activeInterfaceId]
  );

  const addInterface = useCallback(() => {
    const created = createInterface({
      modelId: `dtmi:example:Interface${interfaces.length + 1};1`,
      displayName: `Interface ${interfaces.length + 1}`,
      properties: [],
    });
    setInterfaces((current) => [...current, created]);
    setActiveInterfaceId(created.id);
  }, [interfaces.length]);

  const updateCollection = useCallback(
    (
      interfaceId: string,
      key: keyof InterfaceItem,
      itemId: string,
      patch: any
    ) => {
      updateInterface(interfaceId, (entry) => ({
        ...entry,
        [key]: (entry[key] as any[]).map((item: any) =>
          item.id === itemId ? { ...item, ...patch } : item
        ),
      }));
    },
    [updateInterface]
  );

  const removeFromCollection = useCallback(
    (interfaceId: string, key: keyof InterfaceItem, itemId: string) => {
      updateInterface(interfaceId, (entry) => ({
        ...entry,
        [key]: (entry[key] as any[]).filter((item: any) => item.id !== itemId),
      }));
    },
    [updateInterface]
  );

  const addItem = useCallback(
    (interfaceId: string, key: keyof InterfaceItem, factory: () => any) => {
      updateInterface(interfaceId, (entry) => ({
        ...entry,
        [key]: [...(entry[key] as any[]), factory()],
      }));
    },
    [updateInterface]
  );

  const addRelationshipProperty = useCallback(
    (interfaceId: string, relationshipId: string) => {
      updateInterface(interfaceId, (entry) => ({
        ...entry,
        relationships: entry.relationships.map((r) =>
          r.id === relationshipId
            ? { ...r, properties: [...r.properties, createProperty()] }
            : r
        ),
      }));
    },
    [updateInterface]
  );

  const updateRelationshipProperty = useCallback(
    (
      interfaceId: string,
      relationshipId: string,
      propertyId: string,
      patch: any
    ) => {
      updateInterface(interfaceId, (entry) => ({
        ...entry,
        relationships: entry.relationships.map((r) =>
          r.id === relationshipId
            ? {
                ...r,
                properties: r.properties.map((p) =>
                  p.id === propertyId ? { ...p, ...patch } : p
                ),
              }
            : r
        ),
      }));
    },
    [updateInterface]
  );

  const removeRelationshipProperty = useCallback(
    (interfaceId: string, relationshipId: string, propertyId: string) => {
      updateInterface(interfaceId, (entry) => ({
        ...entry,
        relationships: entry.relationships.map((r) =>
          r.id === relationshipId
            ? {
                ...r,
                properties: r.properties.filter((p) => p.id !== propertyId),
              }
            : r
        ),
      }));
    },
    [updateInterface]
  );

  const addObjectField = useCallback(
    (interfaceId: string, propertyId: string) => {
      updateInterface(interfaceId, (entry) => ({
        ...entry,
        properties: entry.properties.map((p) =>
          p.id === propertyId
            ? { ...p, objectFields: [...p.objectFields, createObjectField()] }
            : p
        ),
      }));
    },
    [updateInterface]
  );

  const updateObjectField = useCallback(
    (interfaceId: string, propertyId: string, fieldId: string, patch: any) => {
      updateInterface(interfaceId, (entry) => ({
        ...entry,
        properties: entry.properties.map((p) =>
          p.id === propertyId
            ? {
                ...p,
                objectFields: p.objectFields.map((f) =>
                  f.id === fieldId ? { ...f, ...patch } : f
                ),
              }
            : p
        ),
      }));
    },
    [updateInterface]
  );

  const removeObjectField = useCallback(
    (interfaceId: string, propertyId: string, fieldId: string) => {
      updateInterface(interfaceId, (entry) => ({
        ...entry,
        properties: entry.properties.map((p) =>
          p.id === propertyId
            ? {
                ...p,
                objectFields: p.objectFields.filter((f) => f.id !== fieldId),
              }
            : p
        ),
      }));
    },
    [updateInterface]
  );

  const addEnumValue = useCallback(
    (interfaceId: string, propertyId: string) => {
      updateInterface(interfaceId, (entry) => ({
        ...entry,
        properties: entry.properties.map((p) =>
          p.id === propertyId
            ? { ...p, enumValues: [...p.enumValues, createEnumValue()] }
            : p
        ),
      }));
    },
    [updateInterface]
  );

  const updateEnumValueItem = useCallback(
    (interfaceId: string, propertyId: string, enumId: string, patch: any) => {
      updateInterface(interfaceId, (entry) => ({
        ...entry,
        properties: entry.properties.map((p) =>
          p.id === propertyId
            ? {
                ...p,
                enumValues: p.enumValues.map((ev) =>
                  ev.id === enumId ? { ...ev, ...patch } : ev
                ),
              }
            : p
        ),
      }));
    },
    [updateInterface]
  );

  const removeEnumValue = useCallback(
    (interfaceId: string, propertyId: string, enumId: string) => {
      updateInterface(interfaceId, (entry) => ({
        ...entry,
        properties: entry.properties.map((p) =>
          p.id === propertyId
            ? {
                ...p,
                enumValues: p.enumValues.filter((ev) => ev.id !== enumId),
              }
            : p
        ),
      }));
    },
    [updateInterface]
  );

  // ── Generate ────────────────────────────────────────────────────────────

  const generateModels = useCallback(() => {
    setStatusMessage('');
    if (validation.hasErrors) {
      setError('Resolve validation errors before generating DTDL JSON.');
      return;
    }
    try {
      const builtInterfaces = interfaces.map((entry) => {
        const trimmedId = entry.modelId.trim();
        const trimmedDisplayName = entry.displayName.trim();
        if (!trimmedId)
          throw new Error('Each interface must include a DTMI in Model ID.');
        if (!trimmedDisplayName)
          throw new Error(`Interface ${trimmedId} is missing displayName.`);
        if (!trimmedId.startsWith('dtmi:'))
          throw new Error(`Interface ${trimmedId} is not a valid DTMI.`);

        const contents: any[] = [];

        entry.properties
          .filter((p) => p.name.trim())
          .forEach((p) => {
            contents.push(
              toPropertyContent(
                p,
                dtdlVersion,
                `Interface ${trimmedId}, property ${p.name.trim()}`
              )
            );
          });

        entry.relationships
          .filter((r) => r.name.trim())
          .forEach((r) => {
            if (r.target.trim() && !r.target.trim().startsWith('dtmi:')) {
              throw new Error(
                `Relationship ${r.name.trim()} target must start with dtmi:.`
              );
            }
            const relItem: any = {
              '@type': 'Relationship',
              name: r.name.trim(),
            };
            if (r.relationshipId.trim())
              relItem['@id'] = r.relationshipId.trim();
            if (r.displayName.trim())
              relItem.displayName = r.displayName.trim();
            if (r.target.trim()) relItem.target = r.target.trim();
            const relProps = r.properties
              .filter((p) => p.name.trim())
              .map((p) =>
                toPropertyContent(
                  p,
                  dtdlVersion,
                  `Relationship ${r.name.trim()} property ${p.name.trim()}`
                )
              );
            if (relProps.length > 0) relItem.properties = relProps;
            contents.push(relItem);
          });

        entry.components
          .filter((c) => c.name.trim() && c.schema.trim())
          .forEach((c) => {
            if (!c.schema.trim().startsWith('dtmi:'))
              throw new Error(
                `Component ${c.name.trim()} schema must be a DTMI.`
              );
            contents.push({
              '@type': 'Component',
              name: c.name.trim(),
              schema: c.schema.trim(),
            });
          });

        const model: any = {
          '@id': trimmedId,
          '@type': 'Interface',
          '@context': CONTEXTS[dtdlVersion],
          displayName: trimmedDisplayName,
          contents,
        };

        const extendsValues = parseExtends(entry.extendsText);
        const invalidParent = extendsValues.find((v) => !v.startsWith('dtmi:'));
        if (invalidParent)
          throw new Error(
            `Extends value ${invalidParent} must start with dtmi:.`
          );
        if (extendsValues.length > 0) model.extends = extendsValues;
        return model;
      });

      const { orderedModels, orderedIds, cycleDetected } =
        orderInterfacesByDependencies(builtInterfaces);
      setGeneratedJson(JSON.stringify(orderedModels, null, 2));
      setDependencyOrder(orderedIds);
      setError('');
      setStatusMessage(
        cycleDetected
          ? 'Generated with fallback order for cyclic dependencies.'
          : 'Generated with dependency-resolved interface ordering.'
      );
    } catch (buildError: any) {
      setError(buildError.message);
    }
  }, [interfaces, dtdlVersion, validation.hasErrors]);

  const copyJson = useCallback(async () => {
    if (!generatedJson) {
      setError('Generate DTDL JSON before copying.');
      return;
    }
    await navigator.clipboard.writeText(generatedJson);
    setStatusMessage('JSON copied to clipboard.');
    setError('');
  }, [generatedJson]);

  const downloadJson = useCallback(() => {
    if (!generatedJson) {
      setError('Generate DTDL JSON before downloading.');
      return;
    }
    const blob = new Blob([generatedJson], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'dtdl-models.json';
    link.click();
    URL.revokeObjectURL(url);
    setStatusMessage('JSON file download started.');
    setError('');
  }, [generatedJson]);

  // ── Render ──────────────────────────────────────────────────────────────

  return (
    <Stack spacing={2.5}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, md: 2.5 },
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Stack spacing={2.25}>
          <Box
            sx={{
              display: 'grid',
              gap: 1.5,
              gridTemplateColumns: {
                xs: '1fr',
                md: 'repeat(3, minmax(0, 1fr))',
              },
            }}
          >
            <TextField
              select
              label="DTDL Version"
              value={dtdlVersion}
              onChange={(e) => setDtdlVersion(e.target.value)}
            >
              <MenuItem value="v2">DTDL v2</MenuItem>
              <MenuItem value="v3">DTDL v3</MenuItem>
            </TextField>
          </Box>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <Typography variant="h6">Interfaces</Typography>
            <Button
              variant="outlined"
              startIcon={<AddCircleOutlineIcon />}
              onClick={addInterface}
            >
              Add Interface
            </Button>
          </Box>

          <Tabs
            value={activeInterface.id}
            onChange={(_, value) => setActiveInterfaceId(value)}
            variant="scrollable"
            scrollButtons="auto"
          >
            {interfaces.map((entry, index) => {
              const tabStatus: InterfaceValidationStatus = validation
                .interfaceStatus[entry.id] ?? {
                hasErrors: false,
                hasWarnings: false,
                errorCount: 0,
                warningCount: 0,
              };
              let chipColor: 'success' | 'warning' | 'error' = 'success';
              let chipLabel = 'Ready';
              if (tabStatus.hasErrors) {
                chipColor = 'error';
                chipLabel = `${tabStatus.errorCount} error${
                  tabStatus.errorCount === 1 ? '' : 's'
                }`;
              } else if (tabStatus.hasWarnings) {
                chipColor = 'warning';
                chipLabel = `${tabStatus.warningCount} warning${
                  tabStatus.warningCount === 1 ? '' : 's'
                }`;
              }
              return (
                <Tab
                  key={entry.id}
                  value={entry.id}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <span>{`${index + 1}. ${
                        entry.displayName || 'Unnamed'
                      }`}</span>
                      <Chip size="small" color={chipColor} label={chipLabel} />
                    </Box>
                  }
                />
              );
            })}
          </Tabs>

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ sm: 'center' }}
            spacing={1}
          >
            <Typography variant="body1" fontWeight={600}>
              Inline messages:
            </Typography>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={validationVisibility}
              onChange={(_, value) => {
                if (value) setValidationVisibility(value);
              }}
            >
              <ToggleButton value="all">Show all</ToggleButton>
              <ToggleButton value="errors">Only errors</ToggleButton>
              <ToggleButton value="warnings">Only warnings</ToggleButton>
            </ToggleButtonGroup>
          </Stack>

          <Stack
            direction={{ xs: 'column', md: 'row' }}
            justifyContent="space-between"
            spacing={1}
          >
            <Typography variant="h5" fontWeight={600}>
              Interface Metadata
            </Typography>
            <Button
              color="error"
              variant="outlined"
              startIcon={<DeleteOutlineIcon />}
              onClick={() => removeInterface(activeInterface.id)}
              disabled={interfaces.length <= 1}
            >
              Remove Interface
            </Button>
          </Stack>

          <Box
            sx={{
              display: 'grid',
              gap: 1.5,
              gridTemplateColumns: {
                xs: '1fr',
                md: 'repeat(3, minmax(0, 1fr))',
              },
            }}
          >
            <Box>
              <TextField
                fullWidth
                label="Model DTMI"
                value={activeInterface.modelId}
                onChange={(e) =>
                  updateInterface(activeInterface.id, {
                    modelId: e.target.value,
                  })
                }
                placeholder="dtmi:example:MyInterface;1"
                inputProps={{ maxLength: 128 }}
              />
              <FieldWarnings
                messages={activeValidation.modelId}
                visibility={validationVisibility}
              />
            </Box>
            <Box>
              <TextField
                fullWidth
                label="Display Name"
                value={activeInterface.displayName}
                onChange={(e) =>
                  updateInterface(activeInterface.id, {
                    displayName: e.target.value,
                  })
                }
                placeholder="My Interface"
                inputProps={{ maxLength: 128 }}
              />
              <FieldWarnings
                messages={activeValidation.displayName}
                visibility={validationVisibility}
              />
            </Box>
            <Box>
              <TextField
                fullWidth
                label="Extends (comma-separated DTMIs)"
                value={activeInterface.extendsText}
                onChange={(e) =>
                  updateInterface(activeInterface.id, {
                    extendsText: e.target.value,
                  })
                }
                placeholder="dtmi:digitaltwins:isa95:HierarchyScope;1"
              />
              <FieldWarnings
                messages={activeValidation.extendsText}
                visibility={validationVisibility}
              />
            </Box>
          </Box>

          <FieldWarnings
            messages={activeValidation.summary}
            visibility={validationVisibility}
          />

          {/* ── Properties ──────────────────────────────────────────────── */}

          <Stack
            direction={{ xs: 'column', md: 'row' }}
            justifyContent="space-between"
            spacing={1}
          >
            <Typography variant="h5" fontWeight={600}>
              Properties
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                startIcon={<PlaylistAddIcon />}
                onClick={() =>
                  addItem(activeInterface.id, 'properties', createProperty)
                }
              >
                Add Property
              </Button>
            </Stack>
          </Stack>

          <Stack spacing={1.5}>
            {activeInterface.properties.length === 0 && (
              <Alert severity="info" variant="outlined">
                No properties yet. Click Add Property.
              </Alert>
            )}

            {activeInterface.properties.map((property) => (
              <Card key={property.id} variant="outlined">
                <CardContent sx={{ minHeight: 'unset', p: 2, '&&': { pb: 2 } }}>
                  <Box
                    sx={{
                      display: 'grid',
                      gap: 1.5,
                      gridTemplateColumns: {
                        xs: '1fr',
                        md: '1.5fr 1fr 1.5fr 1fr auto',
                      },
                    }}
                  >
                    <Box>
                      <TextField
                        fullWidth
                        label="Property name"
                        value={property.name}
                        onChange={(e) =>
                          updateCollection(
                            activeInterface.id,
                            'properties',
                            property.id,
                            {
                              name: e.target.value,
                            }
                          )
                        }
                      />
                      <FieldWarnings
                        messages={
                          activeValidation.properties[property.id]?.name ?? []
                        }
                        visibility={validationVisibility}
                      />
                    </Box>

                    {/* <Box>
                      <TextField
                        fullWidth
                        select
                        label="Schema kind"
                        value={property.schemaKind}
                        onChange={(e) =>
                          updateCollection(
                            activeInterface.id,
                            'properties',
                            property.id,
                            {
                              schemaKind: e.target.value,
                            }
                          )
                        }
                      >
                        {SCHEMA_KIND_OPTIONS.map((sk) => (
                          <MenuItem key={sk.value} value={sk.value}>
                            {sk.label}
                          </MenuItem>
                        ))}
                      </TextField>
                      <FieldWarnings
                        messages={
                          activeValidation.properties[property.id]
                            ?.schemaKind ?? []
                        }
                        visibility={validationVisibility}
                      />
                    </Box> */}
                    {/* default schema type is 'primitive' */}

                    {property.schemaKind === 'primitive' && (
                      <Box>
                        <TextField
                          fullWidth
                          select
                          label="Primitive Schema"
                          value={property.primitiveSchema}
                          onChange={(e) =>
                            updateCollection(
                              activeInterface.id,
                              'properties',
                              property.id,
                              { primitiveSchema: e.target.value }
                            )
                          }
                        >
                          {PRIMITIVE_SCHEMA_OPTIONS.map((s) => (
                            <MenuItem key={s} value={s}>
                              {s}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Box>
                    )}

                    {property.schemaKind === 'array' && (
                      <Box>
                        <TextField
                          fullWidth
                          select
                          label="Array Element Schema"
                          value={property.arrayElementSchema}
                          onChange={(e) =>
                            updateCollection(
                              activeInterface.id,
                              'properties',
                              property.id,
                              { arrayElementSchema: e.target.value }
                            )
                          }
                        >
                          {PRIMITIVE_SCHEMA_OPTIONS.map((s) => (
                            <MenuItem key={s} value={s}>
                              {s}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Box>
                    )}

                    {property.schemaKind !== 'primitive' &&
                      property.schemaKind !== 'array' && <Box />}

                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={property.writable}
                          onChange={(e) =>
                            updateCollection(
                              activeInterface.id,
                              'properties',
                              property.id,
                              { writable: e.target.checked }
                            )
                          }
                        />
                      }
                      label="Writable"
                    />

                    <Box sx={{ alignSelf: 'flex-start', justifySelf: 'end' }}>
                      <Button
                        color="error"
                        variant="outlined"
                        startIcon={<DeleteOutlineIcon />}
                        onClick={() =>
                          removeFromCollection(
                            activeInterface.id,
                            'properties',
                            property.id
                          )
                        }
                      >
                        Remove
                      </Button>
                    </Box>
                  </Box>

                  {(property.schemaKind === 'map' ||
                    property.schemaKind === 'object' ||
                    property.schemaKind === 'enum') && (
                    <Stack spacing={1.5}>
                      {property.schemaKind === 'map' && (
                        <Paper
                          variant="outlined"
                          sx={{ p: 1.5, backgroundColor: 'grey.50' }}
                        >
                          <Stack spacing={1.5}>
                            <Box
                              sx={{
                                display: 'grid',
                                gap: 1.5,
                                gridTemplateColumns: {
                                  xs: '1fr',
                                  md: 'repeat(2, minmax(0, 1fr))',
                                },
                              }}
                            >
                              <TextField
                                select
                                label="Map Key Schema"
                                value={property.mapKeySchema}
                                onChange={(e) =>
                                  updateCollection(
                                    activeInterface.id,
                                    'properties',
                                    property.id,
                                    { mapKeySchema: e.target.value }
                                  )
                                }
                              >
                                {PRIMITIVE_SCHEMA_OPTIONS.map((s) => (
                                  <MenuItem key={s} value={s}>
                                    {s}
                                  </MenuItem>
                                ))}
                              </TextField>
                              <TextField
                                select
                                label="Map Value Schema"
                                value={property.mapValueSchema}
                                onChange={(e) =>
                                  updateCollection(
                                    activeInterface.id,
                                    'properties',
                                    property.id,
                                    { mapValueSchema: e.target.value }
                                  )
                                }
                              >
                                {PRIMITIVE_SCHEMA_OPTIONS.map((s) => (
                                  <MenuItem key={s} value={s}>
                                    {s}
                                  </MenuItem>
                                ))}
                              </TextField>
                            </Box>
                          </Stack>
                        </Paper>
                      )}

                      {property.schemaKind === 'object' && (
                        <Paper
                          variant="outlined"
                          sx={{ p: 1.5, backgroundColor: 'grey.50' }}
                        >
                          <Stack spacing={1.5}>
                            <Stack
                              direction="row"
                              justifyContent="space-between"
                              alignItems="center"
                            >
                              <Typography variant="h6">
                                Object Fields
                              </Typography>
                              <Button
                                variant="outlined"
                                startIcon={<AddCircleOutlineIcon />}
                                onClick={() =>
                                  addObjectField(
                                    activeInterface.id,
                                    property.id
                                  )
                                }
                              >
                                Add Field
                              </Button>
                            </Stack>
                            {property.objectFields.map((field) => (
                              <Box
                                key={field.id}
                                sx={{
                                  display: 'grid',
                                  gap: 1.5,
                                  gridTemplateColumns: {
                                    xs: '1fr',
                                    md: '2fr 1.5fr auto',
                                  },
                                }}
                              >
                                <Box>
                                  <TextField
                                    fullWidth
                                    label="Field name"
                                    value={field.name}
                                    onChange={(e) =>
                                      updateObjectField(
                                        activeInterface.id,
                                        property.id,
                                        field.id,
                                        { name: e.target.value }
                                      )
                                    }
                                  />
                                  <FieldWarnings
                                    messages={
                                      activeValidation.properties[
                                        property.id
                                      ]?.[`field-${field.id}`] ?? []
                                    }
                                    visibility={validationVisibility}
                                  />
                                </Box>
                                <TextField
                                  fullWidth
                                  select
                                  label="Field schema"
                                  value={field.schema}
                                  onChange={(e) =>
                                    updateObjectField(
                                      activeInterface.id,
                                      property.id,
                                      field.id,
                                      { schema: e.target.value }
                                    )
                                  }
                                >
                                  {PRIMITIVE_SCHEMA_OPTIONS.map((s) => (
                                    <MenuItem key={s} value={s}>
                                      {s}
                                    </MenuItem>
                                  ))}
                                </TextField>
                                <Box sx={{ alignSelf: 'flex-start', justifySelf: 'end' }}>
                                  <Button
                                    color="error"
                                    variant="outlined"
                                    startIcon={<DeleteOutlineIcon />}
                                    onClick={() =>
                                      removeObjectField(
                                        activeInterface.id,
                                        property.id,
                                        field.id
                                      )
                                    }
                                  >
                                    Remove
                                  </Button>
                                </Box>
                              </Box>
                            ))}
                          </Stack>
                        </Paper>
                      )}

                      {property.schemaKind === 'enum' && (
                        <Paper
                          variant="outlined"
                          sx={{ p: 1.5, backgroundColor: 'grey.50' }}
                        >
                          <Stack spacing={1.5}>
                            <TextField
                              select
                              label="Enum Value Schema"
                              value={property.enumValueSchema}
                              onChange={(e) =>
                                updateCollection(
                                  activeInterface.id,
                                  'properties',
                                  property.id,
                                  { enumValueSchema: e.target.value }
                                )
                              }
                            >
                              <MenuItem value="string">string</MenuItem>
                              <MenuItem value="integer">integer</MenuItem>
                            </TextField>
                            <Stack
                              direction="row"
                              justifyContent="space-between"
                              alignItems="center"
                            >
                              <Typography variant="h6">Enum Values</Typography>
                              <Button
                                variant="outlined"
                                startIcon={<AddCircleOutlineIcon />}
                                onClick={() =>
                                  addEnumValue(activeInterface.id, property.id)
                                }
                              >
                                Add Enum Value
                              </Button>
                            </Stack>
                            {property.enumValues.map((enumValue) => (
                              <Box
                                key={enumValue.id}
                                sx={{
                                  display: 'grid',
                                  gap: 1.5,
                                  gridTemplateColumns: {
                                    xs: '1fr',
                                    md: '2fr 1.5fr auto',
                                  },
                                }}
                              >
                                <Box>
                                  <TextField
                                    fullWidth
                                    label="Enum name"
                                    value={enumValue.name}
                                    onChange={(e) =>
                                      updateEnumValueItem(
                                        activeInterface.id,
                                        property.id,
                                        enumValue.id,
                                        { name: e.target.value }
                                      )
                                    }
                                  />
                                  <FieldWarnings
                                    messages={
                                      activeValidation.properties[
                                        property.id
                                      ]?.[`enum-${enumValue.id}`] ?? []
                                    }
                                    visibility={validationVisibility}
                                  />
                                </Box>
                                <TextField
                                  fullWidth
                                  label="Enum value"
                                  value={enumValue.enumValue}
                                  onChange={(e) =>
                                    updateEnumValueItem(
                                      activeInterface.id,
                                      property.id,
                                      enumValue.id,
                                      { enumValue: e.target.value }
                                    )
                                  }
                                />
                                <Box sx={{ alignSelf: 'flex-start', justifySelf: 'end' }}>
                                  <Button
                                    color="error"
                                    variant="outlined"
                                    startIcon={<DeleteOutlineIcon />}
                                    onClick={() =>
                                      removeEnumValue(
                                        activeInterface.id,
                                        property.id,
                                        enumValue.id
                                      )
                                    }
                                  >
                                    Remove
                                  </Button>
                                </Box>
                              </Box>
                            ))}
                          </Stack>
                        </Paper>
                      )}
                    </Stack>
                  )}
                </CardContent>
              </Card>
            ))}
          </Stack>

          {/* ── Relationships ───────────────────────────────────────────── */}

          <Stack
            direction={{ xs: 'column', md: 'row' }}
            justifyContent="space-between"
            spacing={1}
          >
            <Typography variant="h5" fontWeight={600}>
              Relationships
            </Typography>
            <Button
              variant="outlined"
              startIcon={<PlaylistAddIcon />}
              onClick={() =>
                addItem(activeInterface.id, 'relationships', createRelationship)
              }
            >
              Add Relationship
            </Button>
          </Stack>

          <Stack spacing={1.5}>
            {activeInterface.relationships.length === 0 && (
              <Alert severity="info" variant="outlined">
                No relationships yet. Click Add Relationship.
              </Alert>
            )}

            {activeInterface.relationships.map((relationship) => (
              <Card key={relationship.id} variant="outlined">
                <CardContent>
                  <Stack spacing={1.5}>
                    <Box
                      sx={{
                        display: 'grid',
                        gap: 1.5,
                        gridTemplateColumns: {
                          xs: '1fr',
                          md: 'repeat(2, minmax(0, 1fr))',
                        },
                      }}
                    >
                      <Box>
                        <TextField
                          fullWidth
                          label="Relationship name"
                          value={relationship.name}
                          onChange={(e) =>
                            updateCollection(
                              activeInterface.id,
                              'relationships',
                              relationship.id,
                              { name: e.target.value }
                            )
                          }
                        />
                        <FieldWarnings
                          messages={
                            activeValidation.relationships[relationship.id]
                              ?.name ?? []
                          }
                          visibility={validationVisibility}
                        />
                      </Box>
                      <TextField
                        fullWidth
                        label="displayName (optional)"
                        value={relationship.displayName}
                        onChange={(e) =>
                          updateCollection(
                            activeInterface.id,
                            'relationships',
                            relationship.id,
                            { displayName: e.target.value }
                          )
                        }
                      />
                      <TextField
                        fullWidth
                        label="@id (optional DTMI)"
                        value={relationship.relationshipId}
                        onChange={(e) =>
                          updateCollection(
                            activeInterface.id,
                            'relationships',
                            relationship.id,
                            { relationshipId: e.target.value }
                          )
                        }
                      />
                      <Box>
                        <TextField
                          fullWidth
                          label="target DTMI (optional)"
                          value={relationship.target}
                          onChange={(e) =>
                            updateCollection(
                              activeInterface.id,
                              'relationships',
                              relationship.id,
                              { target: e.target.value }
                            )
                          }
                        />
                        <FieldWarnings
                          messages={
                            activeValidation.relationships[relationship.id]
                              ?.target ?? []
                          }
                          visibility={validationVisibility}
                        />
                      </Box>
                    </Box>

                    <Paper
                      variant="outlined"
                      sx={{ p: 1.5, backgroundColor: 'grey.50' }}
                    >
                      <Stack spacing={1.5}>
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="center"
                        >
                          <Typography variant="h6">
                            Relationship Properties
                          </Typography>
                          <Button
                            variant="outlined"
                            startIcon={<AddCircleOutlineIcon />}
                            onClick={() =>
                              addRelationshipProperty(
                                activeInterface.id,
                                relationship.id
                              )
                            }
                          >
                            Add Property
                          </Button>
                        </Stack>
                        <Stack spacing={1.5}>
                          {relationship.properties.map((property) => (
                            <Box
                              key={property.id}
                              sx={{
                                display: 'grid',
                                gap: 1.5,
                                gridTemplateColumns: {
                                  xs: '1fr',
                                  md: '2fr 1.5fr auto',
                                },
                              }}
                            >
                              <Box>
                                <TextField
                                  fullWidth
                                  label="Property name"
                                  value={property.name}
                                  onChange={(e) =>
                                    updateRelationshipProperty(
                                      activeInterface.id,
                                      relationship.id,
                                      property.id,
                                      { name: e.target.value }
                                    )
                                  }
                                />
                                <FieldWarnings
                                  messages={
                                    activeValidation.relationships[
                                      relationship.id
                                    ]?.[`property-${property.id}`] ?? []
                                  }
                                  visibility={validationVisibility}
                                />
                              </Box>
                              <TextField
                                fullWidth
                                select
                                label="Schema"
                                value={property.primitiveSchema}
                                onChange={(e) =>
                                  updateRelationshipProperty(
                                    activeInterface.id,
                                    relationship.id,
                                    property.id,
                                    {
                                      primitiveSchema: e.target.value,
                                      schemaKind: 'primitive',
                                    }
                                  )
                                }
                              >
                                {PRIMITIVE_SCHEMA_OPTIONS.map((s) => (
                                  <MenuItem key={s} value={s}>
                                    {s}
                                  </MenuItem>
                                ))}
                              </TextField>
                              <Box sx={{ alignSelf: 'flex-start', justifySelf: 'end' }}>
                                <Button
                                  color="error"
                                  variant="outlined"
                                  startIcon={<DeleteOutlineIcon />}
                                  onClick={() =>
                                    removeRelationshipProperty(
                                      activeInterface.id,
                                      relationship.id,
                                      property.id
                                    )
                                  }
                                >
                                  Remove
                                </Button>
                              </Box>
                            </Box>
                          ))}
                        </Stack>
                      </Stack>
                    </Paper>

                    <Box sx={{ alignSelf: 'flex-end' }}>
                      <Button
                        color="error"
                        variant="outlined"
                        startIcon={<DeleteOutlineIcon />}
                        onClick={() =>
                          removeFromCollection(
                            activeInterface.id,
                            'relationships',
                            relationship.id
                          )
                        }
                      >
                        Remove Relationship
                      </Button>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            ))}
          </Stack>

          {/* ── Components ──────────────────────────────────────────────── */}

          <Stack
            direction={{ xs: 'column', md: 'row' }}
            justifyContent="space-between"
            spacing={1}
          >
            <Typography variant="h5" fontWeight={600}>
              Components
            </Typography>
            <Button
              variant="outlined"
              startIcon={<PlaylistAddIcon />}
              onClick={() =>
                addItem(activeInterface.id, 'components', createComponent)
              }
            >
              Add Component
            </Button>
          </Stack>

          <Stack spacing={1.5}>
            {activeInterface.components.length === 0 && (
              <Alert severity="info" variant="outlined">
                No components yet. Click Add Component.
              </Alert>
            )}

            {activeInterface.components.map((component) => (
              <Card key={component.id} variant="outlined">
                <CardContent>
                  <Box
                    sx={{
                      display: 'grid',
                      gap: 1.5,
                      gridTemplateColumns: { xs: '1fr', md: '2fr 2fr auto' },
                    }}
                  >
                    <Box>
                      <TextField
                        fullWidth
                        label="Component name"
                        value={component.name}
                        onChange={(e) =>
                          updateCollection(
                            activeInterface.id,
                            'components',
                            component.id,
                            { name: e.target.value }
                          )
                        }
                      />
                      <FieldWarnings
                        messages={
                          activeValidation.components[component.id]?.name ?? []
                        }
                        visibility={validationVisibility}
                      />
                    </Box>
                    <Box>
                      <Autocomplete
                        freeSolo
                        options={knownInterfaceDtmis}
                        value={component.schema}
                        onChange={(_, value) =>
                          updateCollection(
                            activeInterface.id,
                            'components',
                            component.id,
                            { schema: value ?? '' }
                          )
                        }
                        onInputChange={(_, value) =>
                          updateCollection(
                            activeInterface.id,
                            'components',
                            component.id,
                            { schema: value }
                          )
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Component interface DTMI"
                            placeholder="dtmi:example:ComponentInterface;1"
                          />
                        )}
                      />
                      <FieldWarnings
                        messages={
                          activeValidation.components[component.id]?.schema ??
                          []
                        }
                        visibility={validationVisibility}
                      />
                    </Box>
                    <Box sx={{ alignSelf: 'flex-start', justifySelf: 'end' }}>
                      <Button
                        color="error"
                        variant="outlined"
                        startIcon={<DeleteOutlineIcon />}
                        onClick={() =>
                          removeFromCollection(
                            activeInterface.id,
                            'components',
                            component.id
                          )
                        }
                      >
                        Remove
                      </Button>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Stack>

          {/* ── Generate Actions ────────────────────────────────────────── */}

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
            <Button
              variant="contained"
              startIcon={<DataObjectIcon />}
              onClick={generateModels}
            >
              Generate DTDL JSON
            </Button>
            <Button
              variant="outlined"
              startIcon={<ContentCopyIcon />}
              onClick={copyJson}
              disabled={!generatedJson}
            >
              Copy JSON
            </Button>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={downloadJson}
              disabled={!generatedJson}
            >
              Download JSON
            </Button>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<CloudUploadIcon />}
              onClick={openUploadModal}
            >
              Upload to ADT
            </Button>
          </Stack>

          {/* ── Upload to ADT Modal ──────────────────────────────────────── */}
          <ModalDialog
            open={uploadModalOpen}
            onClose={() => setUploadModalOpen(false)}
            onConfirm={handleModalUpload}
            title="Upload DTDL Model to ADT"
            cancelBtnText="Close"
            confirmBtnText={isModalUploading ? 'Uploading...' : 'Upload to ADT'}
            disabled={!uploadFile || isModalUploading}
            loading={isModalUploading}
            maxWidth="sm"
            fullWidth
          >
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Choose a local JSON file or use the currently generated DTDL
                JSON, then upload it to Azure Digital Twins.
              </Typography>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<CloudUploadIcon />}
                >
                  Choose JSON File
                  <input
                    type="file"
                    accept=".json,application/json"
                    hidden
                    onChange={(e) => {
                      const file = e.target.files?.[0] ?? null;
                      setUploadFile(file);
                      setUploadModalError('');
                    }}
                  />
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<ArticleIcon />}
                  onClick={useGeneratedJsonAsUploadFile}
                  disabled={!generatedJson}
                >
                  Use Generated JSON
                </Button>
              </Stack>

              <TextField
                fullWidth
                label="Selected file"
                value={uploadFile ? uploadFile.name : 'No file selected'}
                InputProps={{ readOnly: true }}
              />

              {uploadModalError && (
                <Alert severity="error" variant="outlined">
                  {uploadModalError}
                </Alert>
              )}

              {uploadModalResult && (
                <Stack spacing={1}>
                  <Alert
                    severity={
                      uploadModalResult.statusCode >= 200 &&
                      uploadModalResult.statusCode < 300
                        ? 'success'
                        : 'error'
                    }
                    variant="outlined"
                  >
                    {uploadModalResult.statusCode === 409
                      ? 'Duplicate model — a model with this DTMI already exists in ADT'
                      : `Upload response status: ${uploadModalResult.statusCode}`}
                  </Alert>
                  {uploadModalResult.statusCode === 409 && (
                    <Alert severity="info" variant="outlined">
                      Increment the version in the Model DTMI (e.g.{' '}
                      <strong>;1</strong> → <strong>;2</strong>), then
                      re-generate and upload again.
                    </Alert>
                  )}
                  <Box
                    component="pre"
                    sx={{
                      m: 0,
                      p: 2,
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      backgroundColor: '#0b1220',
                      color: '#d9ecff',
                      overflowX: 'auto',
                      fontSize: '0.85rem',
                      fontFamily: '"JetBrains Mono", "Consolas", monospace',
                      maxHeight: 200,
                    }}
                  >
                    {uploadModalResult.responseBody}
                  </Box>
                </Stack>
              )}
            </Stack>
          </ModalDialog>

          {error && (
            <Alert severity="error" variant="outlined">
              {error}
            </Alert>
          )}
          {statusMessage && (
            <Alert severity="success" variant="outlined">
              {statusMessage}
            </Alert>
          )}
          {activeStatus.hasErrors && (
            <Alert severity="error" variant="outlined">
              Active interface has {activeStatus.errorCount} blocking error
              {activeStatus.errorCount === 1 ? '' : 's'}.
            </Alert>
          )}

          {uploadResult && (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Alert
                severity={
                  uploadResult.statusCode >= 200 &&
                  uploadResult.statusCode < 300
                    ? 'success'
                    : 'error'
                }
                variant="outlined"
                sx={{ mb: 1.5 }}
              >
                {uploadResult.statusCode === 409
                  ? 'Duplicate model — a model with this DTMI already exists in ADT'
                  : `Upload response status: ${uploadResult.statusCode}`}
              </Alert>
              {uploadResult.statusCode === 409 && (
                <Alert severity="info" variant="outlined" sx={{ mb: 1.5 }}>
                  To resolve this, increment the version in the Model DTMI (e.g.
                  change <strong>;1</strong> to <strong>;2</strong>), then
                  re-generate and upload again.
                </Alert>
              )}
              <Box
                component="pre"
                sx={{
                  m: 0,
                  p: 2,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  backgroundColor: '#0b1220',
                  color: '#d9ecff',
                  overflowX: 'auto',
                  fontSize: '0.85rem',
                  fontFamily: '"JetBrains Mono", "Consolas", monospace',
                }}
              >
                {uploadResult.responseBody}
              </Box>
              {uploadResult.statusCode >= 400 && (
                <Button
                  variant="outlined"
                  startIcon={<CloudUploadIcon />}
                  onClick={() => handleUploadModel({})}
                  disabled={!generatedJson || isUploading}
                  sx={{ mt: 1.5 }}
                >
                  Retry Upload
                </Button>
              )}
            </Paper>
          )}
        </Stack>
      </Paper>

      {/* ── Generated Output ──────────────────────────────────────────── */}

      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, md: 2.5 },
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Stack spacing={2}>
          <Box>
            <Typography variant="h5" fontWeight={600}>
              Generated DTDL
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Multi-interface output with dependency ordering
            </Typography>
          </Box>

          {dependencyOrder.length > 0 && (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle1" fontWeight={700}>
                Dependency Order
              </Typography>
              <Stack component="ol" sx={{ pl: 2, mb: 0 }} spacing={0.5}>
                {dependencyOrder.map((id) => (
                  <Typography component="li" key={id} variant="body2">
                    {id}
                  </Typography>
                ))}
              </Stack>
            </Paper>
          )}

          <Box
            component="pre"
            sx={{
              m: 0,
              p: 2,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              backgroundColor: '#0b1220',
              color: '#d9ecff',
              overflowX: 'auto',
              fontSize: '0.85rem',
              fontFamily: '"JetBrains Mono", "Consolas", monospace',
            }}
          >
            {generatedJson || 'Generated JSON will appear here.'}
          </Box>
        </Stack>
      </Paper>
    </Stack>
  );
}
